import axios from "axios";
import {
  GET_TENANTS,
  GET_TENANTS_ERROR,
  TENANTS_LOADING,
  SAVE_TENANT,
  DELETE_TENANT,
  TENANT_REPORT,
  TENANT_REPORT_LOADING,
  TENANT_REPORT_ERROR,
} from "./types";
import { toastr } from "react-redux-toastr";
import { logout } from "./auth";
import { mailToast } from "../utils/mailToast";

// Tenants rent kiosks and only read their report. Admins with the tenants key
// manage them behind /api/tenant; a tenant reads GET /api/monitoring/tenant-report.

// Shows the backend's { errors: [{ msg }] } and logs out on an expired token
const handleError = (err, dispatch) => {
  // No response: the network or the server is down
  if (!err.response) {
    toastr.error("Сервертэй холбогдож чадсангүй");
    return;
  }
  if (
    err.response.status === 401 &&
    err.response.data.msg === "Token is not valid"
  ) {
    window.localStorage.clear("x-auth-token");
    toastr.error("Session expired");
    dispatch(logout());
  }
  const errors = err.response.data.errors;
  if (errors) {
    errors.forEach((error) => toastr.error(error.msg));
  }
};

const config = {
  headers: {
    "Content-Type": "application/json",
  },
};

//  Every tenant with their kiosks, and every kiosk one can be given
export const getTenants = () => async (dispatch) => {
  try {
    dispatch({ type: TENANTS_LOADING });
    const res = await axios.get("/api/tenant");
    dispatch({
      type: GET_TENANTS,
      payload: res.data,
    });
  } catch (err) {
    dispatch({ type: GET_TENANTS_ERROR });
    handleError(err, dispatch);
  }
};

//  Creates (no id) or saves a tenant with their kiosks, as
//  { name, email, printers: [{ printerId, since: "YYYY-MM-DD" }] }. A new
//  tenant is mailed an invite to set their own password. Returns { tenant } or
//  the backend's { errors }, for the form to show.
export const saveTenant = (id, body) => async (dispatch) => {
  try {
    const res = id
      ? await axios.put(`/api/tenant/${id}`, JSON.stringify(body), config)
      : await axios.post("/api/tenant", JSON.stringify(body), config);
    const { mailed, ...tenant } = res.data;
    dispatch({
      type: SAVE_TENANT,
      payload: tenant,
    });
    if (id) toastr.success("Хадгалагдлаа");
    else mailToast(tenant.email, "урилга", mailed);
    return { tenant };
  } catch (err) {
    if (!err.response || !err.response.data.errors) handleError(err, dispatch);
    return {
      errors: (err.response && err.response.data.errors) || [],
    };
  }
};

//  Mails the tenant a new link to set their password: their invite again while
//  they have not set one, otherwise a reset
export const sendTenantPasswordLink = (id) => async (dispatch) => {
  try {
    const res = await axios.post(`/api/tenant/${id}/password-link`);
    mailToast(
      res.data.email,
      res.data.pending ? "урилга" : "нууц үг сэргээх холбоос",
      res.data.mailed
    );
    return true;
  } catch (err) {
    handleError(err, dispatch);
    return false;
  }
};

//  Removes the tenant; they are logged out at once
export const deleteTenant = (id) => async (dispatch) => {
  try {
    await axios.delete(`/api/tenant/${id}`);
    dispatch({
      type: DELETE_TENANT,
      payload: id,
    });
    toastr.success("Устгагдлаа");
    return true;
  } catch (err) {
    handleError(err, dispatch);
    return false;
  }
};

// Only the newest report request may fill the page, so a slow answer for an
// earlier range does not replace the one picked after it
let latestReport = 0;

//  The signed-in tenant's report for the days from and to ("YYYY-MM-DD"), both
//  included
export const getTenantReport = (from, to) => async (dispatch) => {
  const request = ++latestReport;
  dispatch({ type: TENANT_REPORT_LOADING });
  try {
    const res = await axios.get("/api/monitoring/tenant-report", {
      params: { from, to },
    });
    if (request !== latestReport) return;
    dispatch({
      type: TENANT_REPORT,
      payload: { report: res.data, updatedAt: new Date() },
    });
  } catch (err) {
    if (request !== latestReport) return;
    dispatch({ type: TENANT_REPORT_ERROR });
    handleError(err, dispatch);
  }
};
